// src/utils/sendVerificationEmail.js
//
// WHY THIS EXISTS
// ---------------
// Both Register.js and VerifyEmail.js called sendEmailVerification() with an
// actionCodeSettings object:
//
//     sendEmailVerification(user, { url: `${window.location.origin}/dashboard` })
//
// That `url` is a *continue URL*, and Firebase refuses to send the email at
// all unless the URL's domain is listed under
// Authentication → Settings → Authorized domains. When it isn't, the call
// throws `auth/unauthorized-continue-uri` and NO EMAIL IS SENT.
//
// In Register.js that throw landed in the same catch block as
// createUserWithEmailAndPassword, which produced the exact symptom reported:
// the account was created in Firebase Auth and in Firestore, then the form
// said "Could not save your details" and no email arrived. Registration had
// in fact succeeded — only the last step failed, and it failed loudly enough
// to look like the whole thing did.
//
// This helper makes the continue URL optional. If the domain isn't
// authorized, it retries with no actionCodeSettings at all — that form of the
// call has no domain to validate and always works. The link in the email then
// lands on Firebase's own "email verified" page instead of bouncing back to
// /dashboard, which is a cosmetic loss, not a broken signup. VerifyEmail
// polls every four seconds, so the person's open tab moves them along anyway.
//
// ⚠️ THE REAL FIX IS IN THE CONSOLE, NOT HERE
// Firebase Console → Authentication → Settings → Authorized domains →
// Add domain. Add every host you serve the app from:
//
//     localhost                        (added by default)
//     sbleducationalportal.web.app     (added by default)
//     sbleducationalportal.firebaseapp.com
//     <your custom domain, with and without www>
//
// Until that's done the fallback below keeps signups working; after it's
// done the fallback simply never fires.

import { sendEmailVerification } from "firebase/auth";

const CONTINUE_PATH = "/dashboard";

// Errors that mean "the continue URL was the problem", as opposed to
// something genuinely wrong with the account or the network.
const CONTINUE_URL_ERRORS = new Set([
  "auth/unauthorized-continue-uri",
  "auth/invalid-continue-uri",
  "auth/missing-continue-uri",
  "auth/unauthorized-domain",
]);

/**
 * Sends the confirmation email, degrading gracefully if this host isn't an
 * authorized domain.
 *
 * @param {import("firebase/auth").User} user
 * @returns {Promise<{sent: true, withContinueUrl: boolean}>}
 * @throws only for real failures (no user, network down, rate limited)
 */
export async function sendVerificationEmail(user) {
  if (!user) throw new Error("No signed-in user to send verification to.");

  try {
    await sendEmailVerification(user, {
      url: `${window.location.origin}${CONTINUE_PATH}`,
      handleCodeInApp: false,
    });
    return { sent: true, withContinueUrl: true };
  } catch (err) {
    if (!CONTINUE_URL_ERRORS.has(err?.code)) throw err;

    console.warn(
      `[verification] "${window.location.hostname}" is not listed under ` +
        `Firebase Console → Authentication → Settings → Authorized domains, ` +
        `so the continue URL was rejected (${err.code}). Sending the email ` +
        `without one — the link will finish on Firebase's default page ` +
        `rather than returning to ${CONTINUE_PATH}. Add the domain to ` +
        `restore the redirect.`
    );

    // No actionCodeSettings: nothing to validate, so this always goes out.
    await sendEmailVerification(user);
    return { sent: true, withContinueUrl: false };
  }
}

export default sendVerificationEmail;
